import React, { useEffect, useState } from 'react';
import './VersePreview.css';
import TeachingTip from './TeachingTip';
import './TeachingTip.css';
import * as DatabaseUtil from '../utils/databaseUtil';
import * as FallbackUtil from '../utils/databaseUtilFallback';

/**
 * VersePreview Component - Displays Quran verses with optional teaching tips
 * 
 * @param {Object} props
 * @param {Array} props.verses - Array of verse objects to display
 * @param {Object} props.teachingTips - Object containing teaching tips with keys in format "surahId:ayahNumber"
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onClose - Function to call when close button is clicked
 */
const VersePreview = ({ verses = [], teachingTips = {}, loading = false, onClose }) => {
  const [localTeachingTips, setLocalTeachingTips] = useState(teachingTips);
  const [loadingTips, setLoadingTips] = useState(false);

  // Initialize database and fetch tips when component mounts or verses change
  useEffect(() => {
    const initAndFetchTips = async () => {
      // Always try to fetch the tips for the current verses
      if (verses.length > 0) {
        setLoadingTips(true);
        try {
          // Try to initialize translation data with both approaches
          const mainInitResult = await DatabaseUtil.initDatabase().catch(err => {
            console.warn('Main database init failed:', err);
            return false;
          });
          
          if (!mainInitResult) {
            console.log('Trying fallback database initialization');
            await FallbackUtil.initDatabase();
          }
          
          // Try to load the database from both sources
          let db = {};
          try {
            db = await DatabaseUtil.getDatabase();
            console.log(`Database loaded with ${Object.keys(db).length} translations`);
          } catch (mainDbError) {
            console.warn('Failed to load main database:', mainDbError);
            try {
              db = await FallbackUtil.getDatabase();
              console.log(`Fallback database loaded with ${Object.keys(db).length} translations`);
            } catch (fallbackDbError) {
              console.error('Both database loading approaches failed:', fallbackDbError);
            }
          }
          
          // Add hardcoded translations for testing (fallback if data isn't loading correctly)
          const hardcodedTips = {
            "1:1": { t: "In the name of Allah, the All-Merciful, the Bestower of mercy" },
            "1:2": { t: "All praise be to Allah, Lord of all realms" },
            "1:3": { t: "The All-Merciful, the Bestower of mercy" },
            "1:4": { t: "Master of the Day of Recompense" },
            "1:5": { t: "It is You we worship, and it is You we call for help" },
            "1:6": { t: "Guide us to the straight path" },
            "1:7": { t: "The path of those You have blessed, not those who have incurred (Your) wrath, nor those who have gone astray" },
          };
          
          // For each verse, try to fetch its tip
          const newTips = {};
          
          for (const verse of verses) {
            const surahId = verse.surah?.number;
            const ayahNumber = verse.numberInSurah;
            
            if (surahId && ayahNumber) {
              const tipKey = `${surahId}:${ayahNumber}`;
              
              console.log(`Checking for translation of ${tipKey}`);
              
              // Check database first
              if (db[tipKey] && db[tipKey].t) {
                console.log(`Found tip in database for ${tipKey}: ${db[tipKey].t.substring(0, 30)}...`);
                newTips[tipKey] = db[tipKey];
              } 
              // Then check parent-passed tips
              else if (teachingTips[tipKey] && teachingTips[tipKey].t) {
                console.log(`Found tip from parent props for ${tipKey}: ${teachingTips[tipKey].t.substring(0, 30)}...`);
                newTips[tipKey] = teachingTips[tipKey];
              }
              // Finally use hardcoded tips for Surah 1 (Al-Fatiha)
              else if (hardcodedTips[tipKey]) {
                console.log(`Using hardcoded tip for ${tipKey}: ${hardcodedTips[tipKey].t}`);
                newTips[tipKey] = hardcodedTips[tipKey];
              }
              else {
                console.log(`No translation found for ${tipKey}`);
              }
            }
          }
          
          if (Object.keys(newTips).length > 0) {
            console.log(`Setting ${Object.keys(newTips).length} teaching tips`);
            setLocalTeachingTips(newTips);
          } else {
            console.log("No teaching tips found for any verses");
          }
        } catch (error) {
          console.error("Error initializing database or fetching tips:", error);
        } finally {
          setLoadingTips(false);
        }
      } else if (Object.keys(teachingTips).length > 0 && teachingTips !== localTeachingTips) {
        // If parent passed new teachingTips, update our local state
        setLocalTeachingTips(teachingTips);
      }
    };
    
    initAndFetchTips();
  }, [verses, teachingTips]);

  if (loading) {
    return (
      <div className="verse-preview-container">
        <div className="verse-preview-header">
          <h2>Verse Preview</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading verses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="verse-preview-container">
      <div className="verse-preview-header">
        <h2>Verse Preview</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      <div className="verse-preview-content">
        {verses.length === 0 ? (
          <p className="no-verses">No verses to display. Please select a valid verse range.</p>
        ) : (
          verses.map((verse) => {
            const surahId = verse.surah?.number;
            const ayahNumber = verse.numberInSurah;
            const tipKey = `${surahId}:${ayahNumber}`;
            const tipText = localTeachingTips[tipKey]?.t;
            
            return (
              <div key={`verse-${verse.number}`} className="verse-item">
                <div className="verse-number">Verse {verse.numberInSurah}</div>
                <div className="verse-text">{verse.text}</div>
                
                {/* Show loading indicator if we're still loading tips */}
                {loadingTips ? (
                  <div className="teaching-tip-loading">Loading translation...</div>
                ) : (
                  /* Render TeachingTip component if tip is available */
                  tipText ? (
                    <TeachingTip text={tipText} />
                  ) : (
                    <div className="no-translation-message">
                      <TeachingTip text="Translation not available for this verse" initialExpanded={false} />
                    </div>
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VersePreview;